/**
 * The wire shapes this page reads and writes, and the vocabulary its controls
 * use.
 *
 * Kept beside the component rather than inside it because they are the page's
 * CONTRACT, not its behaviour: the session mapper, the visit recorder and the
 * specs all need them, and every one of those importing a 2,000-line component
 * to name a field is what made the component the only place they could live.
 *
 * Each interface mirrors a server DTO; the comment on it says which. When one
 * of these drifts from its DTO, the page compiles and lies, so the mirror is
 * the thing being written down here.
 */
export interface SalesRouteStop {
  id: number;
  objectType: string;
  objectId: number;
  title: string | null;
  address: string | null;
  seq: number;
  pinned: boolean;
  pinReason: string | null;
  lat: number | null;
  lng: number | null;
  plannedArrival: string | null;
  driveSecondsFromPrevious: number | null;
  driveMetersFromPrevious: number | null;
  serviceMinutes: number | null;
  status: string;
  outcome: string | null;
  reasons: string[];
  visitedAt: string | null;
  note: string | null;
}

export interface SalesRoute {
  id: number;
  ownerUserId: number;
  routeDate: string;
  status: string;
  source: string;
  pathGeoJson: string | null;
  startAddress?: string | null;
  endAddress?: string | null;
  startLat: number | null;
  startLng: number | null;
  endLat: number | null;
  endLng: number | null;
  originSource: string | null;
  returnsToStart: boolean;
  startTime: string | null;
  endTime: string | null;
  plannedStopCount: number;
  plannedDriveSeconds: number;
  plannedDriveMeters: number;
  finalLegSeconds: number;
  finalLegMeters: number;
  plannedServiceSeconds: number;
  startedAt: string | null;
  completedAt: string | null;
  stops: SalesRouteStop[];
}

export interface VisitDraft {
  memo: string;
  outcome: string | null;
  contactName: string | null;
  nextActionType: string | null;
  nextActionDue: string | null;
  transcript: string;
}

export interface RouteEndpointLocation { label: string; latitude: number; longitude: number; }

/** A rep this account may plan for. Source: /api/sales-routes/reps. */
export interface AssignableRep {
  userId: number;
  userName: string | null;
  displayName: string | null;
}

/** One place the patch owes a visit today, before any ordering exists. */
export interface SalesRouteCandidate {
  leadId: number;
  title: string | null;
  address: string | null;
  lat: number;
  lng: number;
  value: number;
  nextActionDue: string | null;
  daysSinceLastActivity: number | null;
  boardColumn: string;
  reasons: string[];
  /** Whose patch this door is in. Present on every row; only worth SHOWING
   *  when the list holds more than one rep's patch. */
  ownerUserId: number | null;
  ownerName: string | null;
}

export interface SalesRouteCandidates {
  items: SalesRouteCandidate[];
}

export interface RosterEntry {
  salesRouteId: number;
  ownerUserId: number;
  ownerName: string | null;
  status: string;
  source: string;
  plannedStops: number;
  visited: number;
  skipped: number;
  rescheduled?: number;
  plannedDriveSeconds: number;
}

/** A cross-rep handover, pending or decided. Mirrors SalesRouteTransferDto. */
export interface SalesRouteTransfer {
  id: number;
  objectType: string;
  objectId: number;
  fromUserId: number | null;
  toUserId: number;
  toTerritoryId: number;
  status: string;
  reason: string | null;
  requestedByUserId: number;
  decidedByUserId: number | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

/** What one Save of a day did. Mirrors SalesRouteDaySaveDto. */
export interface SalesRouteDaySaveResult {
  routeDate: string;
  ownerUserId: number;
  stopsOrdered: number;
  dayAdjustments: number;
  permanentMoves: number;
  pendingRequests: SalesRouteTransfer[];
  summary: string;
  /** The route that was saved; a new route gets its id here. */
  salesRouteId: number | null;
}

/** The permission keys this page's affordances follow. The server enforces
 *  them; these only decide what is worth offering. */
export const MANAGE_ANY = 'SalesRoute.ManageAny';
export const MANAGE_OWN = 'SalesRoute.ManageOwn';

/**
 * The rep's day: where to go, in what order, and why each stop is on the list.
 *
 * Not a collection route. Everything here is a sales visit; the truck side of
 * the product lives in dispatch and shares only the road graph.
 *
 * ## One page, two permission behaviours
 *
 * Design blocks 3g (Admin) and 3h (Sales) are the SAME screen seen through
 * different grants, not two components:
 *
 *  - `SalesRoute.ManageAny` (Admin/Manager) — every rep's day is readable, a
 *    cross-rep move is performed on Save and is labelled `(permanent)`, and the
 *    approvals entry reads "N transfer requests waiting".
 *  - `SalesRoute.ManageOwn` only (Sales) — their own day; reordering, a
 *    temporary add and a temporary removal, each badged `today only`, with a
 *    removed row left on screen struck through reading
 *    "still yours · back tomorrow". A cross-rep move is still OFFERED: it
 *    produces a pending request card. Disabling the control would remove the
 *    only route a rep has to ask.
 *
 * The two branches differ only in wording and in which extra controls appear.
 * Both send the SAME request: the stop order plus a `moves` list. Nothing in the
 * body says "permanent" or "request" — the server decides that from the
 * caller's own grant, so a rep who edits the page's state cannot skip approval.
 *
 * ## Nothing is written before Save
 *
 * Every edit goes through one `EditSession<DayState, DayOp>`, shared by the list
 * and the map, and Save is one `PUT /api/sales-routes/day`. Cancel returns to
 * the last successful save; Undo steps back one operation and `undoAt` drops any
 * single one, which is how a Copilot proposal will be undone on its own.
 */
